import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  ElementFormatType,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  Spread,
} from "lexical";

import { BlockWithAlignableContents } from "@lexical/react/LexicalBlockWithAlignableContents";
import {
  DecoratorBlockNode,
  SerializedDecoratorBlockNode,
} from "@lexical/react/LexicalDecoratorBlockNode";
import * as React from "react";

type AparatComponentProps = Readonly<{
  className: Readonly<{
    base: string;
    focus: string;
  }>;
  format: ElementFormatType | null;
  nodeKey: NodeKey;
  videoID: string;
}>;

function AparatComponent({
  className,
  format,
  nodeKey,
  videoID,
}: AparatComponentProps) {
  return (
    <BlockWithAlignableContents
      className={className}
      format={format}
      nodeKey={nodeKey}
    >
      <iframe
        width="560"
        height="315"
        src={`https://www.aparat.com/video/video/embed/videohash/${videoID}/vt/frame`}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen={true}
        title="ویدیو آپارات"
      />
    </BlockWithAlignableContents>
  );
}

export type SerializedAparatNode = Spread<
  {
    videoID: string;
  },
  SerializedDecoratorBlockNode
>;

function $convertAparatElement(
  domNode: HTMLElement
): null | DOMConversionOutput {
  const videoID = domNode.getAttribute("data-lexical-aparat");
  const formatData = domNode.getAttribute("data-lexical-aparat-format") ?? "";
  let format: ElementFormatType | undefined = undefined;

  if (videoID) {
    if (
      formatData.includes("center") ||
      formatData.includes("right") ||
      formatData.includes("left")
    ) {
      format = formatData as ElementFormatType;
    }

    const node = $createAparatNode(videoID, format);
    return { node };
  }

  return null;
}

export class AparatNode extends DecoratorBlockNode {
  __id: string;

  static getType(): string {
    return "aparat";
  }

  static clone(node: AparatNode): AparatNode {
    return new AparatNode(node.__id, node.__format, node.__key);
  }

  static importJSON(serializedNode: SerializedAparatNode): AparatNode {
    const node = $createAparatNode(serializedNode.videoID);
    node.setFormat(serializedNode.format);
    return node;
  }

  exportJSON(): SerializedAparatNode {
    return {
      ...super.exportJSON(),
      type: "aparat",
      version: 1,
      videoID: this.__id,
    };
  }

  constructor(id: string, format?: ElementFormatType, key?: NodeKey) {
    super(format, key);
    this.__id = id;
  }

  exportDOM(): DOMExportOutput {
    const container = document.createElement("div");
    container.style.textAlign = this.__format;
    container.setAttribute("data-lexical-aparat", this.__id);
    container.setAttribute("data-lexical-aparat-format", this.__format);

    const element = document.createElement("iframe");
    element.setAttribute("data-lexical-aparat", this.__id);
    element.setAttribute("width", "560");
    element.setAttribute("height", "315");
    element.setAttribute(
      "src",
      `https://www.aparat.com/video/video/embed/videohash/${this.__id}/vt/frame`
    );
    element.setAttribute("frameborder", "0");
    element.setAttribute(
      "allow",
      "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
    );
    element.setAttribute("allowfullscreen", "true");
    element.setAttribute("title", "Aparat video");

    container.appendChild(element);

    return { element: container };
    // return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute("data-lexical-aparat")) {
          return null;
        }
        return {
          conversion: $convertAparatElement,
          priority: 1,
        };
      },
    };
  }

  updateDOM(): false {
    return false;
  }

  getId(): string {
    return this.__id;
  }

  getTextContent(
    _includeInert?: boolean | undefined,
    _includeDirectionless?: false | undefined
  ): string {
    return `https://www.aparat.com/v/${this.__id}`;
  }

  decorate(_editor: LexicalEditor, config: EditorConfig): JSX.Element {
    const embedBlockTheme = config.theme.embedBlock || {};
    const className = {
      base: embedBlockTheme.base || "",
      focus: embedBlockTheme.focus || "",
    };
    return (
      <AparatComponent
        className={className}
        format={this.__format}
        nodeKey={this.getKey()}
        videoID={this.__id}
      />
    );
  }
}

export function $createAparatNode(
  videoID: string,
  format?: ElementFormatType
): AparatNode {
  return new AparatNode(videoID, format);
}

export function $isAparatNode(
  node: AparatNode | LexicalNode | null | undefined
): node is AparatNode {
  return node instanceof AparatNode;
}
